import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'designSystem/components/Tooltip'
import { WorkbenchLayout, WorkbenchPanelSide } from 'features/workbench/types'
import { LucideIcon, PanelBottom, PanelLeft, PanelRight } from 'lucide-react'
import React from 'react'
import styled from 'styled-components'

const ToggleTrigger = styled.span`
  display: inline-flex;
`

const SIDE_CONFIG: Record<
  WorkbenchPanelSide,
  { icon: LucideIcon; label: MessageDescriptor }
> = {
  left: { icon: PanelLeft, label: msg`left panel` },
  bottom: { icon: PanelBottom, label: msg`bottom panel` },
  right: { icon: PanelRight, label: msg`right panel` },
}

interface LayoutTogglesProps {
  layout: WorkbenchLayout
  onToggle: (side: WorkbenchPanelSide) => void
  sides?: WorkbenchPanelSide[]
  /** What each panel holds, in place of the generic "left panel" wording. */
  labels?: Partial<Record<WorkbenchPanelSide, string>>
  /**
   * Why a closed panel cannot be opened right now. The toggle is disabled and
   * the reason becomes its tooltip; an open panel can always be hidden.
   */
  disabledReasons?: Partial<Record<WorkbenchPanelSide, string>>
}

export const LayoutToggles: React.FC<LayoutTogglesProps> = ({
  layout,
  onToggle,
  sides = ['left', 'bottom', 'right'],
  labels,
  disabledReasons,
}) => {
  const { i18n, t } = useLingui()
  return (
    <>
      {sides.map((side) => {
        const { icon: Icon, label: defaultLabel } = SIDE_CONFIG[side]
        const label = labels?.[side] ?? i18n._(defaultLabel)
        const on = layout[side]
        const actionLabel = on ? t`Hide ${label}` : t`Show ${label}`
        const disabledReason = on ? undefined : disabledReasons?.[side]
        return (
          <Tooltip key={side}>
            <TooltipTrigger asChild>
              <ToggleTrigger>
                <Button
                  variant='inputRaised'
                  size='icon-lg'
                  pressed='emphasis'
                  aria-pressed={on}
                  aria-label={actionLabel}
                  disabled={disabledReason !== undefined}
                  onClick={() => onToggle(side)}>
                  <Icon size={18} strokeWidth={1.5} />
                </Button>
              </ToggleTrigger>
            </TooltipTrigger>
            <TooltipContent side='bottom'>
              {disabledReason ?? actionLabel}
            </TooltipContent>
          </Tooltip>
        )
      })}
    </>
  )
}
